import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './Dashboard.css';
import { Line } from 'react-chartjs-2';
import { LineElement, PointElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import { useFetch } from "../hooks/useFetch";
import Header from "./common/Header";
import { useTranslation } from 'react-i18next';
import Sidebar from "./common/Sidebar";

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, LineElement, PointElement);

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'es';
  const locale = lang.startsWith('es') ? 'es-CL' : lang.startsWith('en') ? 'en-US' : lang;
  const CURRENCY = 'CLP'; // adjust if you add multi-currency later
  // Ensure KPI cards never span full width when inside CSS grid
  const KPI_CARD_STYLE = { gridColumn: 'auto / span 1', width: 'auto' };
  const [ dashboardData ] = useFetch('/dashboard', { defaultValue: [] });
  const [ expensesData ] = useFetch('/transactions', { defaultValue: [], hasPaging: true, defaultLimit: 500 });
  const [ subsData ] = useFetch('/subscriptions', { defaultValue: [] });
  // Parse Chilean-style amounts like "$10.990" or "10.990" → 10990
  const parseAmount = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return isFinite(val) ? val : 0;
    const s = String(val).trim();
    if (!s) return 0;
    // Remove currency symbols and spaces
    let x = s.replace(/[^0-9.,-]/g, '');
    // If it has dots and no commas, treat dots as thousands separators (CLP common)
    if (x.includes('.') && !x.includes(',')) x = x.replace(/\./g, '');
    // If it has both "," and ".", assume comma is decimal (unlikely in CLP) and strip thousands dots
    if (x.includes(',') && x.includes('.')) x = x.replace(/\./g, '').replace(',', '.');
    // If it only has comma, treat it as thousands or decimal → drop commas
    if (x.includes(',') && !x.includes('.')) x = x.replace(/,/g, '');
    const n = Number(x);
    return isNaN(n) ? 0 : n;
  };

  // Subscriptions: normalize and pick top 3 by monthly cost
  const subscriptions = useMemo(() => {
    const raw = Array.isArray(subsData?.subscriptions)
      ? subsData.subscriptions
      : Array.isArray(subsData?.items)
        ? subsData.items
        : Array.isArray(subsData)
          ? subsData
          : [];
    return raw.map((s, i) => {
      const name = s.name || s.title || s.service || `Subscription ${i+1}`;
      const cycle = (s.cycle || s.frequency || 'monthly').toString().toLowerCase();
      const amountRaw = s.amount ?? s.price ?? s.monthly ?? s.monthly_amount ?? s.amount_clp ?? 0;
      // Support amount in cents
      const amt = typeof amountRaw === 'number' && amountRaw > 100000 ? amountRaw / 100 : parseAmount(amountRaw);
      const status = (s.status || 'active').toString().toLowerCase();
      return { ...s, name, cycle, amount: amt, status };
    });
  }, [subsData]);

  // Normalize any subscription-like item to a monthly amount
  const toMonthly = useCallback((item) => {
    const amt = Number(item.amount ?? item.monthly ?? 0) || 0;
    const cycle = (item.cycle || item.frequency || 'monthly').toString().toLowerCase();
    if (cycle === 'monthly') return amt;
    if (cycle === 'yearly' || cycle === 'annual' || cycle === 'annually') return amt / 12;
    if (cycle === 'weekly') return amt * (52 / 12);
    if (cycle === 'daily') return amt * 30; // rough
    return amt; // default assume monthly
  }, []);

  const [monthlySummary, setMonthlySummary] = useState([]); // [{month, amount}]
  const [categories, setCategories] = useState([]);         // [{name, amount}]
  const [expenses, setExpenses] = useState([]);
  // Range for overview charts: '1m' | '3m' | '6m' | '12m' | 'all'
  const [range, setRange] = useState('6m');
  const [showFixedList, setShowFixedList] = useState(false);
  const [showForecastBreakdown, setShowForecastBreakdown] = useState(false);
  const [showSubscriptionsList, setShowSubscriptionsList] = useState(false);
  const [subsQuery, setSubsQuery] = useState('');
  const [subsSort, setSubsSort] = useState('amount'); // 'amount' | 'name'
  useEffect(() => {
    if (!showSubscriptionsList) {
      setSubsQuery('');
      setSubsSort('amount');
    }
  }, [showSubscriptionsList]);
  const subscriptionsRef = useRef(null);
  const APP_VERSION =
    process.env.REACT_APP_VERSION ||
    (typeof window !== 'undefined' && window.__APP_VERSION__) ||
    'dev';
  const forecastRef = useRef(null);
  const fixedRef = useRef(null);
  // Close subscriptions modal on Escape key
  useEffect(() => {
    if (!showSubscriptionsList) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowSubscriptionsList(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSubscriptionsList]);

  // Close subscriptions modal on outside click
  useEffect(() => {
    if (!showSubscriptionsList) return;
    const handleOutside = (e) => {
      if (subscriptionsRef.current && !subscriptionsRef.current.contains(e.target)) {
        setShowSubscriptionsList(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showSubscriptionsList]);

  // Close fixed expenses list modal on Escape key
  useEffect(() => {
    if (!showFixedList) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowFixedList(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showFixedList]);

  useEffect(() => {
    if (!showFixedList) return;
    const handleOutside = (e) => {
      if (fixedRef.current && !fixedRef.current.contains(e.target)) {
        setShowFixedList(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showFixedList]);

  // Close forecast breakdown modal on Escape key
  useEffect(() => {
    if (!showForecastBreakdown) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowForecastBreakdown(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForecastBreakdown]);

  useEffect(() => {
    if (!showForecastBreakdown) return;
    const handleOutside = (e) => {
      if (forecastRef.current && !forecastRef.current.contains(e.target)) {
        setShowForecastBreakdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showForecastBreakdown]);

  // Normaliza datos del backend
  useEffect(() => {
    if (!dashboardData || (Array.isArray(dashboardData) && dashboardData.length === 0)) return;
    const data = dashboardData.data || dashboardData;

    // categorías
    const cats = Object.entries(data?.categories || {}).map(([name, amount]) => ({ name, amount: Number(amount) || 0 }));
    setCategories(cats);

    // monthly summary (acepta objeto, lista o mapa por tarjeta)
    const src = (data && data.monthly_summary != null) ? data.monthly_summary : data?.monthly_summary_list;
    const padMonth = (m) => { const mm = String(m); const m1 = mm.match(/^(\d{4})-(\d{1,2})$/); return m1 ? `${m1[1]}-${m1[2].padStart(2,'0')}` : mm; };
    let arr = [];
    if (Array.isArray(src)) {
      if (src.length && typeof src[0] === 'object' && src[0] && 'month' in src[0]) arr = src.map(it => ({ month: padMonth(it.month), amount: Number(it.amount) || 0 }));
      else if (Array.isArray(src[0])) arr = src.map(([m,a]) => ({ month: padMonth(m), amount: Number(a) || 0 }));
    } else if (src && typeof src === 'object') {
      const vals = Object.values(src);
      if (vals.every(v => typeof v === 'number' || typeof v === 'string')) {
        arr = Object.entries(src).map(([m,a]) => ({ month: padMonth(m), amount: Number(a)||0 }));
      } else if (vals.every(v => v && typeof v === 'object')) {
        const acc = {};
        Object.values(src).forEach(cardMap => {
          Object.entries(cardMap||{}).forEach(([m,a]) => {
            const mm = padMonth(m);
            acc[mm] = (acc[mm]||0) + (Number(a)||0);
          });
        });
        arr = Object.entries(acc).map(([m,a]) => ({ month: m, amount: a }));
      }
    }
    setMonthlySummary(arr);
  }, [dashboardData]);

  // Expenses (para KPI de cuotas activas)
  useEffect(() => {
    const list = Array.isArray(expensesData.transactions) ? expensesData.transactions
      : Array.isArray(expensesData) ? expensesData : [];
    setExpenses(list);
  }, [expensesData]);

  // Fallback: if backend didn't provide monthly_summary, build it from expenses
  useEffect(() => {
    if (monthlySummary && monthlySummary.length > 0) return; // already have data
    if (!expenses || expenses.length === 0) return;          // need expenses to aggregate

    const padMonth = (m) => {
      const mm = String(m);
      const m1 = mm.match(/^(\d{4})-(\d{1,2})$/);
      return m1 ? `${m1[1]}-${m1[2].padStart(2,'0')}` : mm;
    };

    const totals = {};
    expenses.forEach(e => {
      const raw = (e.date || '').slice(0,7);
      if (!raw) return;
      const key = padMonth(raw);
      if (!/^\d{4}-\d{2}$/.test(key)) return;
      totals[key] = (totals[key] || 0) + (Number(e.amount) || 0);
    });

    const arr = Object.entries(totals).map(([month, amount]) => ({ month, amount }));
    setMonthlySummary(arr);
  }, [expenses, monthlySummary]);

  const monthlySorted = useMemo(
    () => [...monthlySummary].sort((a,b) => new Date(`${a.month}-01`) - new Date(`${b.month}-01`)),
    [monthlySummary]
  );

  // Fallback: detect recurring-like subscriptions from expenses (last 6 months, ≥2 distinct months)
  const detectedSubsAllFromExpenses = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0) return [];
    const monthsWindow = monthlySorted.slice(-6).map(m => m.month);
    if (monthsWindow.length === 0) return [];
    const monthSet = new Set(monthsWindow);

    // Group by merchant/payee (fallback to description)
    const byPayee = new Map();
    for (const e of expenses) {
      const m = (e?.date || '').slice(0, 7);
      if (!monthSet.has(m)) continue;
      const payee = (e?.merchant || e?.data?.merchant || e?.description || '').trim();
      if (!payee) continue;
      const amt = Number(e?.amount) || 0;
      if (!byPayee.has(payee)) byPayee.set(payee, { months: new Map() });
      const rec = byPayee.get(payee);
      rec.months.set(m, (rec.months.get(m) || 0) + amt);
    }

    const out = [];
    byPayee.forEach((rec, name) => {
      const months = Array.from(rec.months.keys());
      if (months.length < 2) return; // need at least 2 months in window
      const total = Array.from(rec.months.values()).reduce((s, v) => s + v, 0);
      const monthly = total / months.length;
      if (monthly <= 0) return;
      out.push({ name, monthly });
    });

    return out.sort((a, b) => b.monthly - a.monthly);
  }, [expenses, monthlySorted]);

  // Unified subscriptions list (backend active subs + detected from expenses), normalized to monthly
  const unifiedSubscriptions = useMemo(() => {
    const api = (subscriptions || [])
      .filter(s => (s.status !== 'paused' && s.status !== 'cancelled'))
      .map(s => ({ name: s.name, monthly: toMonthly(s) }))
      .filter(x => x.monthly > 0);

    const detected = (detectedSubsAllFromExpenses || [])
      .map(x => ({ name: x.name, monthly: Number(x.monthly) || 0 }))
      .filter(x => x.monthly > 0);

    // Merge by name (preferring API amount when both exist)
    const map = new Map();
    for (const it of detected) {
      map.set(it.name, { ...it });
    }
    for (const it of api) {
      const prev = map.get(it.name);
      if (!prev) map.set(it.name, it);
      else map.set(it.name, { name: it.name, monthly: it.monthly || prev.monthly });
    }

    return Array.from(map.values()).sort((a,b) => b.monthly - a.monthly);
  }, [subscriptions, detectedSubsAllFromExpenses, toMonthly]);

  const activeSubsCount = useMemo(() => {
    return (unifiedSubscriptions || []).filter(it => Number(it.monthly) > 0).length;
  }, [unifiedSubscriptions]);

  const activeSubsTotalMonthly = useMemo(() => {
    return (unifiedSubscriptions || []).reduce((sum, it) => sum + (Number(it.monthly) || 0), 0);
  }, [unifiedSubscriptions]);

  const categoriesCount = useMemo(() => {
    if (Array.isArray(categories) && categories.length > 0) return categories.length;
    const set = new Set();
    (expenses || []).forEach(e => {
      const name = e?.data?.subcategory?.category?.name;
      if (name && name !== '—') set.add(name);
    });
    return set.size;
  }, [categories, expenses]);

  const tagsCount = useMemo(() => {
    const extractTags = (expense) => {
      if (!expense) return [];
      const raw = expense.tags ?? expense.data?.tags ?? expense.data?.tags_csv ?? expense.data?.tagsCsv ?? expense.data?.tagsCSV;
      let arr = [];
      if (Array.isArray(raw)) arr = raw;
      else if (typeof raw === 'string') arr = raw.split(',');
      return arr.map(t => String(t).trim()).filter(Boolean);
    };
    const set = new Set();
    (expenses || []).forEach(e => {
      const tags = extractTags(e);
      tags.forEach(t => set.add(t));
    });
    return set.size;
  }, [expenses]);

  // For the modal: include ALL backend active subs (even monthly=0) + a looser detected set (≥2 months, no tolerance)
  const allSubscriptionsForModal = useMemo(() => {
    const apiAll = (subscriptions || [])
      .filter(s => (s.status !== 'paused' && s.status !== 'cancelled'))
      .map(s => ({ name: s.name, monthly: toMonthly(s) }));

    // Looser detection: at least 2 months in last 6
    const monthsWindow = monthlySorted.slice(-6).map(m => m.month);
    const monthSet = new Set(monthsWindow);
    const byPayee = new Map();
    // Helper: normalize payee names
    function normalizePayee(str) {
      return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(/\s+/g, '');
    }
    for (const e of expenses || []) {
      const m = (e?.date || '').slice(0, 7);
      if (!monthSet.has(m)) continue;
      const display = (e?.merchant || e?.data?.merchant || e?.description || '').trim();
      const key = normalizePayee(display);
      if (!key) continue;
      const amt = Number(e?.amount) || 0;
      if (!byPayee.has(key)) byPayee.set(key, { display, months: new Map() });
      const rec = byPayee.get(key);
      if (display.length > rec.display.length) rec.display = display;
      rec.months.set(m, (rec.months.get(m) || 0) + amt);
    }
    const detectedLoose = [];
    byPayee.forEach((rec) => {
      const months = Array.from(rec.months.keys());
      if (months.length < 2) return; // looser than card logic
      const vals = Array.from(rec.months.values());
      const avg = vals.reduce((s,v) => s+v, 0) / vals.length;
      if (avg <= 0) return;
      detectedLoose.push({ name: rec.display, monthly: avg });
    });

    // Merge: prefer API value if exists
    const map = new Map();
    for (const it of detectedLoose) map.set(it.name, { ...it });
    for (const it of apiAll) {
      const prev = map.get(it.name);
      if (!prev) map.set(it.name, it); else map.set(it.name, { name: it.name, monthly: it.monthly || prev.monthly });
    }

    return Array.from(map.values()).sort((a,b) => b.monthly - a.monthly);
  }, [subscriptions, expenses, monthlySorted, toMonthly]);

  const subsModalList = useMemo(() => {
    let arr = allSubscriptionsForModal || [];
    const q = subsQuery.trim().toLowerCase();
    if (q) arr = arr.filter(it => (it.name || '').toLowerCase().includes(q));
    if (subsSort === 'name') arr = [...arr].sort((a,b) => (a.name || '').localeCompare(b.name || ''));
    else arr = [...arr].sort((a,b) => (Number(b.monthly)||0) - (Number(a.monthly)||0));
    return arr;
  }, [allSubscriptionsForModal, subsQuery, subsSort]);

  const filteredMonthly = useMemo(() => {
    if (!monthlySorted.length) return [];
    if (range === 'all') return monthlySorted;
    const N = range === '1m' ? 1 : range === '3m' ? 3 : range === '6m' ? 6 : 12;
    return monthlySorted.slice(-N);
  }, [monthlySorted, range]);

  // Month-over-Month for last available month vs previous month
  const mom = useMemo(() => {
    if (!monthlySorted.length) return null;
    if (monthlySorted.length < 2) return null;
    const last = monthlySorted[monthlySorted.length - 1];
    const prev = monthlySorted[monthlySorted.length - 2];
    const currAmount = last.amount;
    const prevAmount = prev.amount;
    const delta = currAmount - prevAmount;
    const pct = prevAmount === 0 ? null : (delta / prevAmount) * 100;
    return { curr: currAmount, prev: prevAmount, delta, pct, lastMonth: last.month, prevMonth: prev.month };
  }, [monthlySorted]);

  const rangeMonthSet = useMemo(() => new Set(filteredMonthly.map(m => m.month)), [filteredMonthly]);

  const cardBreakdown = useMemo(() => {
    if (!expenses?.length) return [];
    const map = {};
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (!rangeMonthSet.size || rangeMonthSet.has(m)) {
        const label = (e.upload?.card_type === 0 ? 'Visa' : e.upload?.card_type === 1 ? 'Mastercard' : e.upload?.card_type === 2 ? 'American Express' : e.upload?.card_type === 3 ? 'Débito' : (e.upload?.card_type || 'Other'));
        map[label] = (map[label] || 0) + (Number(e.amount) || 0);
      }
    });
    return Object.entries(map).map(([card, amount]) => ({ card, amount })).sort((a,b) => b.amount - a.amount);
  }, [expenses, rangeMonthSet]);

  // KPIs (based on last available data month)
  const lastAvailableKey = monthlySorted.length ? monthlySorted[monthlySorted.length - 1].month : null;
  // Per-month category totals for the last available month
  const categoriesLastMonth = useMemo(() => {
    if (!expenses?.length || !lastAvailableKey) return {};
    const map = {};
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (m !== lastAvailableKey) return;
      const name = e.data?.subcategory?.category?.name || '—';
      map[name] = (map[name] || 0) + (Number(e.amount) || 0);
    });
    return map;
  }, [expenses, lastAvailableKey]);
  // Localized label for last available month, e.g., "Último mes: Dic 2024"
  const lastMonthText = useMemo(() => {
    if (!lastAvailableKey) return t('overview.kpiMonthly', 'This month');
    const [yy, mm] = lastAvailableKey.split('-').map(Number);
    const d = new Date(yy, mm - 1, 1);
    const monthShort = d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }).replace('.', '');
    const pretty = `${monthShort.charAt(0).toUpperCase() + monthShort.slice(1)} ${yy}`;
    // i18n label with fallback
    return t('overview.lastMonth', lang.startsWith('es') ? 'Último mes: {{label}}' : 'Last month: {{label}}', { label: pretty });
  }, [lastAvailableKey, locale, lang, t]);
  const currentMonthAmount = lastAvailableKey ? (monthlySummary.find(m => m.month === lastAvailableKey)?.amount || 0) : 0;
  const lastMonthsN = 6;
  const lastN = monthlySorted.slice(-lastMonthsN);
  const avgLastN = lastN.length ? (lastN.reduce((s, x) => s + x.amount, 0) / lastN.length) : 0;
  const highest = monthlySummary.reduce((acc, x) => (x && x.amount > (acc?.amount || 0) ? x : acc), null);

  // Helper: extract goals into a plain map { name: goal } from various shapes
  const extractGoalsMap = (src) => {
    if (!src) return null;
    let cur = src;
    // Unwrap up to two nested "data" levels
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;

    // Case A: array of objects
    if (Array.isArray(cur)) {
      const map = {};
      cur.forEach(item => {
        const nm = String(item?.category_name ?? item?.name ?? '').trim();
        const g  = Number(item?.goal ?? item?.limit ?? 0) || 0;
        if (nm) map[nm] = g;
      });
      return Object.keys(map).length ? map : null;
    }

    // Case B: object maps on common keys
    const candidates = [
      cur?.goals,
      cur?.categories,
      cur
    ];
    for (const obj of candidates) {
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        const values = Object.values(obj);
        if (values.length && values.every(v => typeof v === 'number' || typeof v === 'string')) {
          const map = {};
          Object.entries(obj).forEach(([k, v]) => {
            const nm = String(k).trim();
            const g  = Number(v) || 0;
            if (nm) map[nm] = g;
          });
          if (Object.keys(map).length) return map;
        }
      }
    }
    return null;
  };
  // ---- Metas (Goals) from backend ----
  const [goalsResp] = useFetch('/goals/categories', { defaultValue: [] });
  // Map { name -> goal }
  const goalsMap = useMemo(() => {
    return extractGoalsMap(goalsResp) || {};
  }, [goalsResp]);
  // Build progress list: spent from each category's last active month vs goal
  const goalsProgress = useMemo(() => {
    // 1. Build per-category month sums from expenses
    const perCat = new Map(); // cat -> Map(month -> sum)
    for (const e of expenses || []) {
      if (!e) continue;
      const catName =
        e.data?.subcategory?.category?.name ||
        e.category_name ||
        e.category ||
        e.categoryName ||
        '—';
      const cat = String(catName).trim();
      if (!cat) continue;
      const month = (e.date || '').slice(0, 7);
      if (!month) continue;
      const amt = Number(e.amount) || 0;
      if (!perCat.has(cat)) perCat.set(cat, new Map());
      const m = perCat.get(cat);
      m.set(month, (m.get(month) || 0) + amt);
    }

    // 2. Build map of category -> spend in its latest month
    const latestPerCat = {};
    for (const [cat, monthMap] of perCat.entries()) {
      const months = Array.from(monthMap.keys());
      if (!months.length) continue;
      months.sort((a, b) => String(b).localeCompare(String(a))); // latest first
      const latest = months[0];
      latestPerCat[cat] = monthMap.get(latest) || 0;
    }

    // 3. Build progress list using each category’s latest active month
    const out = [];
    Object.entries(goalsMap).forEach(([cat, goal]) => {
      const g = Number(goal) || 0;
      if (g <= 0) return;
      const spent = Number(latestPerCat[cat] ?? 0);
      const pct = g > 0 ? (spent / g) * 100 : null;
      out.push({ cat, goal: g, spent, pct });
    });
    return out.sort((a, b) => (b.pct || 0) - (a.pct || 0));
  }, [goalsMap, expenses]);

  // Pending installments count from last available month
  const activeInstallmentsPendingCount = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0 || !lastAvailableKey) return 0;

    const series = new Map();

    for (const e of expenses) {
      if (!e) continue;
      const d = e.data || {};
      const m = (e.date || '').slice(0, 7);
      const seenInLast = m === lastAvailableKey;

      // Prefer structured fields
      let n = Number.isFinite(d.installment_number) ? Number(d.installment_number) : null;
      let total = Number.isFinite(d.installment_total) ? Number(d.installment_total) : null;

      // Fallback: parse from string like "4/12"
      if (total == null || !Number.isFinite(total) || n == null || !Number.isFinite(n)) {
        const raw = e.installment ?? d.installment ?? '';
        if (typeof raw === 'string' && raw.includes('/')) {
          const [a, b] = raw.split('/').map(s => Number(String(s).replace(/[^0-9]/g, '')));
          if (!Number.isFinite(n) && Number.isFinite(a)) n = a;
          if (!Number.isFinite(total) && Number.isFinite(b)) total = b;
        }
      }

      if (!Number.isFinite(total) || total <= 1) continue; // not an installment plan
      if (!Number.isFinite(n)) n = 0;

      // Stable key per installment series
      const key = String(
        d.series_id ||
        e.reference_code ||
        `${e.description || ''}__${total}__${(e.installment_value ?? d.installment_value ?? e.amount ?? '')}`
      );

      const prev = series.get(key);
      const rec = prev ? { ...prev } : { n: n, total: total, seenInLast: false };
      // Keep the **latest** installment number seen
      if (!prev || n > rec.n || total !== rec.total) {
        rec.n = n; rec.total = total;
      }
      // Mark if this series appears in the last available month
      if (seenInLast) rec.seenInLast = true;
      series.set(key, rec);
    }

    let pending = 0;
    series.forEach(({ n, total, seenInLast }) => {
      if (seenInLast && Number.isFinite(n) && Number.isFinite(total) && n < total) {
        pending += (total - n);
      }
    });

    return pending;
  }, [expenses, lastAvailableKey]);

  // Compute total committed in active installments
  const activeInstallmentsCommitted = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0) return 0;

    const series = new Map();

    for (const e of expenses) {
      if (!e) continue;
      const d = e.data || {};

      // Prefer structured fields
      let n = Number.isFinite(d.installment_number) ? Number(d.installment_number) : null;
      let total = Number.isFinite(d.installment_total) ? Number(d.installment_total) : null;
      let perInst = null;
      if (Number.isFinite(d.installment_value)) perInst = Number(d.installment_value);
      else if (Number.isFinite(e.installment_value)) perInst = Number(e.installment_value);

      // Fallback: parse from string like "4/12"
      if (total == null || !Number.isFinite(total) || n == null || !Number.isFinite(n)) {
        const raw = e.installment ?? d.installment ?? '';
        if (typeof raw === 'string' && raw.includes('/')) {
          const [a, b] = raw.split('/').map(s => Number(String(s).replace(/[^0-9]/g, '')));
          if (!Number.isFinite(n) && Number.isFinite(a)) n = a;
          if (!Number.isFinite(total) && Number.isFinite(b)) total = b;
        }
      }

      // Per‑installment fallback (use current amount if nothing else is available)
      if (!Number.isFinite(perInst)) {
        const amt = Number(e.amount);
        if (Number.isFinite(amt)) perInst = amt;
      }

      if (!Number.isFinite(total) || total <= 1) continue; // ignore non‑installment or 1/1
      if (!Number.isFinite(n)) n = 0;
      if (!Number.isFinite(perInst) || perInst <= 0) continue;

      // Stable key for a given series
      const key = String(
        d.series_id ||
        e.reference_code ||
        `${e.description || ''}__${total}__${(e.installment_value ?? d.installment_value ?? e.amount ?? '')}`
      );

      const prev = series.get(key);
      // Keep the latest seen installment index and consistent total/perInst
      if (!prev || n > prev.n || total !== prev.total) {
        series.set(key, { n, total, perInst });
      }
    }

    let committed = 0;
    series.forEach(({ n, total, perInst }) => {
      if (Number.isFinite(n) && Number.isFinite(total) && n < total) {
        const remaining = Math.max(0, total - n); // remaining future installments
        committed += remaining * perInst;
      }
    });
    return committed;
  }, [expenses]);

  const trendData = useMemo(() => ({
    labels: filteredMonthly.map(m => m.month),
    datasets: [{
      label: t('overview.trend', 'Monthly trend'),
      data: filteredMonthly.map(m => m.amount),
      borderColor: '#0056b3',
      borderWidth: 2,
      tension: 0.25,
      pointRadius: 2,
      fill: false
    }]
  }), [filteredMonthly, t]);

  const topCategories = useMemo(() => {
    if (!expenses?.length) return [];
    const byCat = {};
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (!rangeMonthSet.size || rangeMonthSet.has(m)) {
        const name = e.data?.subcategory?.category?.name || '—';
        byCat[name] = (byCat[name] || 0) + (Number(e.amount) || 0);
      }
    });
    return Object.entries(byCat).map(([name, amount]) => ({ name, amount }))
      .sort((a,b) => b.amount - a.amount)
      .slice(0, 5);
  }, [expenses, rangeMonthSet]);

  // Forecast baseline to match Forecast.js: average of monthly totals built from expenses
  const nextMonthForecast = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0) return 0;
    const totalsByMonth = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const amt = Number(e.amount) || 0;
      totalsByMonth[key] = (totalsByMonth[key] || 0) + amt;
    });
    const vals = Object.values(totalsByMonth).map(Number).filter(v => Number.isFinite(v));
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [expenses]);

  // Align next-month key with Forecast.js base: use latest expense date, not lastAvailableKey
  const nextMonthKey = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0) return null;
    const latest = new Date(Math.max(...expenses.map(e => new Date(e.date))));
    const d = new Date(latest.getFullYear(), latest.getMonth(), 1);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, [expenses]);

  // Sum of installments due in the next month (robust parsing, per-installment amount)
  const nextMonthInstallments = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0 || !nextMonthKey) return 0;
    let sum = 0;

    for (const e of expenses) {
      if (!e) continue;
      const d = e.data || {};

      // Prefer structured fields
      let n = Number.isFinite(d.installment_number) ? Number(d.installment_number) : null;
      let total = Number.isFinite(d.installment_total) ? Number(d.installment_total) : null;
      let perInst = null;
      if (Number.isFinite(e.installment_value)) perInst = Number(e.installment_value);
      else if (Number.isFinite(d.installment_value)) perInst = Number(d.installment_value);

      // Fallbacks
      if (total == null || !Number.isFinite(total) || n == null || !Number.isFinite(n)) {
        const raw = e.installment ?? d.installment ?? '';
        if (typeof raw === 'string' && raw.includes('/')) {
          const [a, b] = raw.split('/').map(s => Number(String(s).replace(/[^0-9]/g, '')));
          if (!Number.isFinite(n) && Number.isFinite(a)) n = a;
          if (!Number.isFinite(total) && Number.isFinite(b)) total = b;
        }
      }
      if (!Number.isFinite(perInst)) {
        const amt = Number(e.payment ?? e.amount);
        if (Number.isFinite(amt)) perInst = amt; // many statements already store the installment amount here
      }

      if (!Number.isFinite(total) || total <= 1) continue;
      if (!Number.isFinite(n)) n = 0;
      if (!Number.isFinite(perInst) || perInst <= 0) continue;

      // Generate the schedule from the transaction month
      const start = new Date(e.date);
      for (let i = n + 1; i <= total; i++) {
        const instDate = new Date(start.getFullYear(), start.getMonth() + (i - n), 1);
        const key = `${instDate.getFullYear()}-${String(instDate.getMonth() + 1).padStart(2, '0')}`;
        if (key === nextMonthKey) sum += perInst;
        if (key > nextMonthKey) break; // early exit once we pass target month
      }
    }
    return sum;
  }, [expenses, nextMonthKey]);

  // Combined estimate for next month: projected spend + installments due next month
  const nextMonthTotalWithInstallments = useMemo(() => {
    const proj = Number(nextMonthForecast || 0);
    const inst = Number(nextMonthInstallments || 0);
    return proj + inst;
  }, [nextMonthForecast, nextMonthInstallments]);

  const forecastByCategory = useMemo(() => {
    if (!expenses?.length || !monthlySorted.length || nextMonthForecast == null) return [];
    const months = monthlySorted.slice(-3).map(m => m.month); // last up to 3 months
    if (!months.length) return [];
    const weights = [1, 2, 3].slice(3 - months.length);
    const sumW = weights.reduce((a,b) => a+b, 0);
    // build per-category amounts per month
    const perCatPerMonth = {};
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (!months.includes(m)) return;
      const cat = e.data?.subcategory?.category?.name || '—';
      perCatPerMonth[cat] = perCatPerMonth[cat] || {};
      perCatPerMonth[cat][m] = (perCatPerMonth[cat][m] || 0) + (Number(e.amount)||0);
    });
    // weighted projection per category
    const arr = Object.entries(perCatPerMonth).map(([cat, mm]) => {
      let proj = 0;
      months.forEach((m, idx) => {
        const amt = Number(mm[m] || 0);
        proj += amt * weights[idx];
      });
      proj = proj / sumW;
      // Optional small momentum per category (same 0.15 factor as overall but only if 2+ months)
      if (months.length >= 2) {
        const last = Number(mm[months[months.length-1]] || 0);
        const prev = Number(mm[months[months.length-2]] || 0);
        proj = Math.max(0, proj + (last - prev) * 0.15);
      }
      return { cat, amount: Math.max(0, proj) };
    }).filter(x => x.amount > 0);
    // rescale to match overall forecast
    const sum = arr.reduce((s,x) => s + x.amount, 0);
    if (sum > 0) {
      const k = nextMonthForecast / sum;
      return arr.map(x => ({ ...x, amount: x.amount * k })).sort((a,b) => b.amount - a.amount);
    }
    return arr.sort((a,b) => b.amount - a.amount);
  }, [expenses, monthlySorted, nextMonthForecast]);

  // Alerts: over budget or abnormal deviation vs avg of last 3 months by category
  const categoryAveragesLast3 = useMemo(() => {
    // Build per-category totals for each of the last 3 months
    const months = monthlySorted.slice(-3).map(m => m.month);
    const acc = {};
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (!months.includes(m)) return;
      const name = e.data?.subcategory?.category?.name || '—';
      acc[name] = acc[name] || { months: {}, avg: 0 };
      acc[name].months[m] = (acc[name].months[m] || 0) + (Number(e.amount)||0);
    });
    Object.keys(acc).forEach(name => {
      const vals = Object.values(acc[name].months);
      acc[name].avg = vals.length ? vals.reduce((s,v) => s+v,0) / vals.length : 0;
    });
    return acc;
  }, [expenses, monthlySorted]);

  const alerts = useMemo(() => {
    const items = [];
    // Goal-based alerts (work with backend goals + totals)
    goalsProgress.forEach(g => {
      const pct = g.pct || 0;
      if (pct >= 100) {
        // Meta superada
        items.push({
          type: 'goalOver',
          priority: 1,
          message: t('goals.alertOver', 'Meta superada en {{cat}} ({{pct}}%)', { cat: g.cat, pct: Math.round(pct) })
        });
      } else if (pct >= 85) {
        // Cerca de la meta (umbral configurable)
        items.push({
          type: 'goalNear',
          priority: 2,
          message: t('goals.alertNear', 'Cerca de la meta en {{cat}} ({{pct}}%)', { cat: g.cat, pct: Math.round(pct) })
        });
      }
    });
    // Deviation alerts vs avg last 3 months
    Object.entries(categoriesLastMonth).forEach(([cat, spent]) => {
      const avg = categoryAveragesLast3[cat]?.avg ?? null;
      if (avg != null && avg > 0) {
        const pct = ((spent - avg) / avg) * 100;
        if (pct >= 40) items.push({ type: 'deviation', priority: 3, message: t('overview.alertDeviationHigh', '{{cat}} is {{pct}}% above 3-month avg', { cat, pct: Math.round(pct) }) });
        if (pct <= -40) items.push({ type: 'deviationLow', priority: 4, message: t('overview.alertDeviationLow', '{{cat}} is {{pct}}% below 3-month avg', { cat, pct: Math.round(Math.abs(pct)) }) });
      }
    });
    // Sort by severity: over-goal, near-goal, then deviations
    items.sort((a, b) => (a.priority || 99) - (b.priority || 99));
    return items;
  }, [goalsProgress, categoriesLastMonth, categoryAveragesLast3, t]);

  // Fixed expenses insight (heuristic by category name)
  const FIXED_KEYWORDS = useMemo(() => {
    return [
        // Housing & community
        'renta',
    'arriendo',
    'alquiler',
    'dividendo',
    'hipoteca',
    'mortgage',
    'gastos comunes',
    'contribuciones',
    'administración',
    'administracion',
    'condominio',
    'estacionamiento',
    // Utilities
    'luz',
    'electricidad',
    'electric',
    'agua',
    'water',
    'gas',
    'internet',
    'tv',
    'televisión',
    'television',
    'cable',
    // Telecom
    'teléfono',
    'telefono',
    'phone',
    'móvil',
    'movil',
    'mobile',
    'plan',
    'plan de datos',
    'plan de salud',
    // Insurance & health
    'seguro',
    'seguro auto',
    'seguro hogar',
    'isapre',
    'fonasa',
    'salud',
    'plan de salud',
    // Education & childcare
    'colegio',
    'colegiatura',
    'matrícula',
    'matricula',
    'escolaridad',
    'jardín',
    'jardin',
    'guardería',
    'guarderia',
    'daycare',
    // Transport recurring
    'tag',
    'peaje',
    'peajes',
    // Subscriptions
    'subscription',
    'suscripción',
    'suscripcion',
    'spotify',
    'netflix',
    'prime',
    'disney',
    'hbo',
    'paramount',
    'apple tv',
    'appletv',
    'youtube premium',
    'yt music',
    'ytmusic',
    'icloud',
    'google one',
    'microsoft 365',
    'office 365',
    // Fitness
    'gym',
    'gimnasio'
]
  }, []);
  const fixedShare = useMemo(() => {
    if (!expenses?.length || !lastAvailableKey) return null;
    let fixed = 0, total = 0;
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (m !== lastAvailableKey) return;
      const cat = (e.data?.subcategory?.category?.name || '').toLowerCase();
      const desc = (e.description || '').toLowerCase();
      const isFixed = FIXED_KEYWORDS.some(k => cat.includes(k) || desc.includes(k));
      const amt = Number(e.amount) || 0;
      total += amt; if (isFixed) fixed += amt;
    });
    if (total === 0) return { fixed: 0, total: 0, pct: 0 };
    return { fixed, total, pct: (fixed / total) * 100 };
  }, [expenses, lastAvailableKey, FIXED_KEYWORDS]);

  const fixedItems = useMemo(() => {
    if (!expenses?.length || !lastAvailableKey) return [];
    const out = [];
    expenses.forEach(e => {
      const m = (e.date || '').slice(0,7);
      if (m !== lastAvailableKey) return;
      const cat = (e.data?.subcategory?.category?.name || '').toLowerCase();
      const desc = (e.description || '').toLowerCase();
      const isFixed = FIXED_KEYWORDS.some(k => cat.includes(k) || desc.includes(k));
      if (isFixed) {
        out.push({
          id: e.id || `${e.date}-${e.description}-${e.amount}`,
          date: e.date,
          merchant: e.merchant || e.data?.merchant || e.description || '—',
          category: e.data?.subcategory?.category?.name || '—',
          amount: Number(e.amount) || 0
        });
      }
    });
    // Sort descending by amount
    return out.sort((a,b) => b.amount - a.amount);
  }, [expenses, lastAvailableKey, FIXED_KEYWORDS]);

  const fixedItemsTotal = useMemo(() => {
    return fixedItems.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  }, [fixedItems]);

  return (
    <div className="dashboard-container overview">
      <Header title={t('sidebar.dashboard', 'Dashboard')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main">

        {/* KPI Tiles */}
        <div className="dashboard-section">
          <h2>{t('overview.title', 'Resumen')}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {['1m','3m','6m','12m','all'].map(key => (
              <button key={key} onClick={() => setRange(key)} className={`pretty-toggle ${range === key ? 'active' : ''}`}>
                {key === '1m' ? '1M' : key === '3m' ? '3M' : key === '6m' ? '6M' : key === '12m' ? '12M' : t('common.all', 'Todas')}
              </button>
            ))}
          </div>
          <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))', gap: '1rem' }}>
            <div className="kpi-card" style={KPI_CARD_STYLE}><div>{lastMonthText}</div><strong>{currentMonthAmount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong></div>
            <div className="kpi-card" style={KPI_CARD_STYLE}><div>{t('overview.kpiAvg', 'Promedio (últimos {{n}} meses)', { n: lastMonthsN })}</div><strong>{avgLastN.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong></div>
            <div className="kpi-card" style={KPI_CARD_STYLE}><div>{t('overview.kpiPeak', 'Mes más alto')}</div><strong>{highest ? `${highest.month}: ${highest.amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}` : '—'}</strong></div>
          </div>
        </div>

        {/* Tendencia mensual */}
        <div className="dashboard-section">
          <h2>{t('overview.trend', 'Tendencia mensual')}</h2>
          <div className="dashboard-pie-large">
            <Line
              data={trendData}
              options={{
                plugins: { legend: { display: false } },
                elements: { point: { hitRadius: 8 } },
                scales: {
                  x: { ticks: { maxRotation: 45, minRotation: 45 } },
                  y: { beginAtZero: true }
                }
              }}
            />
          </div>
        </div>

        {/* Top categorías */}
        <div className="dashboard-section">
          <h2>{t('overview.categories', 'Categorías destacadas')}</h2>
          <ul className="top-categories-list">
            {topCategories.map(({ name, amount }) => (
              <li key={name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{name}</span>
                <strong>{amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              </li>
            ))}
            {topCategories.length === 0 && <li style={{ opacity: 0.7 }}>{t('dashboard.noData', 'Sin datos')}</li>}
          </ul>
        </div>

        {/* MoM & Card Breakdown */}
        {mom && (
          <div className="dashboard-section">
            <h2>{t('overview.mom', 'Mes a mes')}</h2>
            <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))', gap: '1rem' }}>
              <div className="kpi-card" style={KPI_CARD_STYLE}>
                <div>{t('overview.currentMonth', 'Mes actual')}</div>
                <strong>{(mom.curr ?? 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              </div>
              <div className="kpi-card" style={KPI_CARD_STYLE}>
                <div>{t('overview.previousMonth', 'Mes anterior')}</div>
                <strong>{(mom.prev ?? 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              </div>
              <div className="kpi-card" style={KPI_CARD_STYLE}>
                <div>{t('overview.delta', 'Δ vs anterior')}</div>
                <strong>
                  {mom ? `${(mom.delta).toLocaleString(locale, { style: 'currency', currency: CURRENCY })}${mom.pct == null ? '' : ` (${mom.pct.toFixed(1)}%)`}` : '—'}
                </strong>
              </div>
            </div>
          </div>
        )}

        {/* Card breakdown over selected range */}
        <div className="dashboard-section">
          <h2>{t('overview.cards', 'Por tipo de tarjeta')}</h2>
          <ul className="top-categories-list">
            {cardBreakdown.map(({ card, amount }) => (
              <li key={card} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{card}</span>
                <strong>{amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              </li>
            ))}
            {cardBreakdown.length === 0 && <li style={{ opacity: 0.7 }}>{t('dashboard.noData', 'Sin datos')}</li>}
          </ul>
        </div>


        {/* Metas (Goals) progress from backend */}
        {Object.keys(goalsMap).length > 0 && (
          <div className="dashboard-section">
            <h2>{t('goals.title', 'Metas')}</h2>
            <ul className="top-categories-list">
              {goalsProgress.map(({ cat, spent, goal, pct }) => (
                <li key={cat} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center' }}>
                  <span>{cat}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {spent.toLocaleString(locale, { style: 'currency', currency: CURRENCY })} / {goal.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                    {pct != null && (
                      <span style={{ marginLeft: 8, opacity: 0.8 }}>({Math.round(pct)}%)</span>
                    )}
                  </span>
                  <div style={{ gridColumn: '1 / -1', height: 8, background: '#e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.min(100, Math.max(0, pct || 0))}%`,
                        height: '100%',
                        background: (pct || 0) >= 100
                          ? '#dc2626' /* red */
                          : (pct || 0) >= 80
                            ? '#f59e0b' /* yellow */
                            : '#16a34a' /* green */
                      }}
                    />
                  </div>
                </li>
              ))}
              {goalsProgress.length === 0 && (
                <li style={{ opacity: 0.7 }}>{t('goals.noGoals', 'Sin metas definidas')}</li>
              )}
            </ul>
          </div>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="dashboard-section">
            <h2>{t('overview.alerts', 'Alertas')}</h2>
            <ul className="top-categories-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {alerts.map((a, i) => (
                <li
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.25em 1fr',
                    columnGap: '8px',
                    alignItems: 'start'
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      alignSelf: 'start',
                      color: a.type === 'goalOver' ? '#dc2626' : a.type === 'goalNear' ? '#f59e0b' : '#374151'
                    }}
                  >
                    —
                  </span>
                  <span>{a.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Fixed expenses insight */}
        {fixedShare && (
          <div className="dashboard-section">
            <h2>{t('overview.fixedInsight', 'Gastos fijos')}</h2>
            <div
              className="full-width-card"
              role="button"
              tabIndex={0}
              onClick={() => setShowFixedList(v => !v)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowFixedList(v => !v); } }}
              aria-expanded={showFixedList}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '0.75rem',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ fontSize: 14, opacity: 0.9 }}>
                  {t('overview.fixedShare', 'Participación de gastos fijos (último mes)')}
                </div>
                <strong style={{ fontSize: 28, lineHeight: 1 }}>
                  {Math.round(fixedShare.pct)}%
                </strong>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
                {t('overview.fixedAmount', '{{fixed}} de {{total}}', {
                  fixed: (fixedShare.fixed || 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY }),
                  total: (fixedShare.total || 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY })
                })}
              </div>
              <div
                style={{
                  marginTop: 10,
                  height: 10,
                  background: '#e5e7eb',
                  borderRadius: 6,
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, Math.max(0, fixedShare.pct || 0))}%`,
                    height: '100%',
                    background:
                      (fixedShare.pct || 0) >= 100
                        ? '#dc2626'
                        : (fixedShare.pct || 0) >= 90
                        ? '#f59e0b'
                        : '#2563eb'
                  }}
                />
              </div>
              {showFixedList && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="fixed-modal-title"
                  onClick={() => setShowFixedList(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}
                >
                  <div
                    ref={fixedRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: 'min(720px, 92vw)',
                      maxHeight: '80vh',
                      overflow: 'auto',
                      background: '#fff',
                      borderRadius: 8,
                      padding: '1rem',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h3 id="fixed-modal-title" style={{ margin: 0 }}>
                        {t('overview.fixedItemsTitle', 'Gastos fijos de este mes')} ({fixedItems.length})
                      </h3>
                      <button
                        onClick={() => setShowFixedList(false)}
                        aria-label={t('common.close', 'Cerrar')}
                        style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                    <ul className="top-categories-list">
                      {fixedItems.map((it) => (
                        <li key={it.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(120px, 1fr) auto', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {new Date(it.date).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}
                          </span>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {it.merchant} <span style={{ opacity: 0.6 }}>· {it.category}</span>
                          </span>
                          <strong style={{ justifySelf: 'end' }}>
                            {it.amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                          </strong>
                        </li>
                      ))}
                      {fixedItems.length === 0 && (
                        <li style={{ opacity: 0.7 }}>{t('overview.noFixedItems', 'No se detectaron gastos fijos este mes')}</li>
                      )}
                    </ul>
                    <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: '0.5rem', marginTop: '0.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>{t('overview.total', 'Total')} ({fixedItems.length})</strong>
                      <strong>{fixedItemsTotal.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick insights: 4 cards in a dedicated container below */}
        <div className="dashboard-section quick-insights" style={{ gridColumn: '1 / span 3', order: -1 }}>
          <h2>{t('overview.quickInsights', 'Insights rápidos')}</h2>
          <div className="quad-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))', gap: '1rem' }}>
            {/* Forecast */}
            <div
              className="kpi-card"
              style={{ ...KPI_CARD_STYLE, cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onClick={() => setShowForecastBreakdown(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowForecastBreakdown(true); } }}
              aria-controls="forecast-modal-title"
              aria-expanded={!!showForecastBreakdown}
            >
              <div>{t('overview.nextMonthForecast', 'Proyección próximo mes')}</div>
              <strong>{nextMonthTotalWithInstallments.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {t('overview.breakdown', 'Incluye cuotas')}: {nextMonthInstallments.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
              </div>
            </div>

            {/* Metas (show highest used or count) */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('goals.title', 'Metas')}</div>
              {goalsProgress && goalsProgress.length > 0 ? (
                <>
                  <strong>
                    {(() => {
                      const top = goalsProgress[0];
                      const pct = Math.round(top?.pct ?? 0);
                      return `${top?.cat ?? '—'}: ${pct}%`;
                    })()}
                  </strong>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    {t('goals.title', 'Metas')}: {goalsProgress.length}
                  </div>
                </>
              ) : (
                <strong>—</strong>
              )}
            </div>

            {/* Alerts count */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('overview.cardAlerts', 'Alertas')}</div>
              <strong>{alerts?.length || 0}</strong>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {alerts?.length ? t('overview.alerts', 'Alertas') : t('dashboard.noData', 'Sin datos')}
              </div>
            </div>

            {/* Fixed share */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('overview.cardFixed', 'Gastos fijos')}</div>
              <strong>{fixedShare ? Math.round(fixedShare.pct) + '%' : '—'}</strong>
              {fixedShare && (
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {t('overview.fixedAmount', '{{fixed}} de {{total}}', {
                    fixed: (fixedShare.fixed||0).toLocaleString(locale, { style: 'currency', currency: CURRENCY }),
                    total: (fixedShare.total||0).toLocaleString(locale, { style: 'currency', currency: CURRENCY })
                  })}
                </div>
              )}
            </div>

            {/* Active installments (Cuotas activas) */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('overview.kpiInstallments', 'Cuotas activas')}</div>
              <strong>{activeInstallmentsPendingCount.toLocaleString(locale)}</strong>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {t('overview.installmentsCommitted', 'Comprometido')}: {activeInstallmentsCommitted.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
              </div>
            </div>
            {/* Active subscriptions count */}
            <div
              className="kpi-card"
              style={{ ...KPI_CARD_STYLE, cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onClick={() => setShowSubscriptionsList(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSubscriptionsList(true); } }}
              title={t('overview.subscriptions', 'Suscripciones')}
            >
              <div>{t('overview.activeSubsCount', 'Suscripciones activas')}</div>
              <strong>{activeSubsCount}</strong>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {t('overview.activeSubsCommitted', 'Mensual')}: {activeSubsTotalMonthly.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
              </div>
            </div>
            {/* Categories count */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('overview.categories', 'Categorías')}</div>
              <strong>{categoriesCount}</strong>
            </div>

            {/* Tags count */}
            <div className="kpi-card" style={KPI_CARD_STYLE}>
              <div>{t('overview.tags', 'Etiquetas')}</div>
              <strong>{tagsCount}</strong>
            </div>
          </div>
        </div>

        {/* Subscriptions overview (new dedicated card) */}
        <div className="dashboard-section">
          <h2>{t('overview.subscriptions', 'Suscripciones')}</h2>
          <div
            className="full-width-card"
            role="button"
            tabIndex={0}
            onClick={() => setShowSubscriptionsList(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSubscriptionsList(true); } }}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '0.75rem',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, opacity: 0.9 }}>
                {t('overview.topSubscriptions', 'Suscripciones destacadas')}
              </div>
            </div>
            {(() => {
              const effectiveTop = (unifiedSubscriptions && unifiedSubscriptions.length)
                ? unifiedSubscriptions.slice(0, 3)
                : [];
              return (
                <ul className="top-categories-list" style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.95rem' }}>
                  {effectiveTop.map(({ name, monthly }) => (
                    <li key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                      <strong style={{ whiteSpace: 'nowrap' }}>
                        {monthly.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                      </strong>
                    </li>
                  ))}
                  {effectiveTop.length === 0 && (
                    <li style={{ opacity: 0.7 }}>{t('dashboard.noData', 'Sin datos')}</li>
                  )}
                </ul>
              );
            })()}
            {showSubscriptionsList && (
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="subscriptions-modal-title"
                onClick={() => setShowSubscriptionsList(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}
              >
                <div
                  ref={subscriptionsRef}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: 'min(720px, 92vw)',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    background: '#fff',
                    borderRadius: 8,
                    padding: '1rem',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <h3 id="subscriptions-modal-title" style={{ margin: 0 }}>
                      {t('overview.subscriptionsDetailTitle', 'All subscriptions')} ({subsModalList.length})
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
                      <input
                        type="text"
                        value={subsQuery}
                        onChange={(e) => setSubsQuery(e.target.value)}
                        placeholder={t('common.search', 'Buscar...')}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          padding: '6px 8px',
                          fontSize: 14
                        }}
                        aria-label={t('common.search', 'Buscar')}
                      />
                      <div className="btn-group" role="group" aria-label="sort modes" style={{ display: 'inline-flex', gap: 4 }}>
                        <button
                          className={`pretty-toggle ${subsSort === 'amount' ? 'active' : ''}`}
                          onClick={() => setSubsSort('amount')}
                        >
                          {t('common.amount', 'Monto')}
                        </button>
                        <button
                          className={`pretty-toggle ${subsSort === 'name' ? 'active' : ''}`}
                          onClick={() => setSubsSort('name')}
                        >
                          {t('common.name', 'Nombre')}
                        </button>
                      </div>
                      <button
                        onClick={() => setShowSubscriptionsList(false)}
                        aria-label={t('common.close', 'Cerrar')}
                        style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <ul className="top-categories-list">
                    {subsModalList.map((it, i) => (
                      <li key={it.name + i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</span>
                        <strong style={{ justifySelf: 'end' }}>
                          {it.monthly.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                        </strong>
                      </li>
                    ))}
                    {subsModalList.length === 0 && (
                      <li style={{ opacity: 0.7 }}>{t('dashboard.noData', 'Sin datos')}</li>
                    )}
                  </ul>
                  <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: '0.5rem', marginTop: '0.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>{t('overview.total', 'Total')}</strong>
                    <strong>
                      {(subsModalList.reduce((s, it) => s + (Number(it.monthly) || 0), 0)).toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showForecastBreakdown && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forecast-modal-title"
            onClick={() => setShowForecastBreakdown(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div
              ref={forecastRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(720px, 92vw)',
                maxHeight: '80vh',
                overflow: 'auto',
                background: '#fff',
                borderRadius: 8,
                padding: '1rem',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 id="forecast-modal-title" style={{ margin: 0 }}>
                  {t('overview.forecastBreakdown', 'Proyección por categoría (próximo mes)')} · {t('overview.breakdown', 'Incluye cuotas')}
                </h3>
                <button
                  onClick={() => setShowForecastBreakdown(false)}
                  aria-label={t('common.close', 'Cerrar')}
                  style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              <ul className="top-categories-list">
                {forecastByCategory.map(({ cat, amount }) => (
                  <li key={cat} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{cat}</span>
                    <strong>{amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
                  </li>
                ))}
                {Number(nextMonthInstallments) > 0 && (
                  <li key="__installments__" style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.85 }}>
                    <span>{t('overview.installmentsShort', 'Cuotas del mes')}</span>
                    <strong>{nextMonthInstallments.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
                  </li>
                )}
                {forecastByCategory.length === 0 && (
                  <li style={{ opacity: 0.7 }}>{t('dashboard.noData', 'Sin datos')}</li>
                )}
              </ul>
              <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: '0.5rem', marginTop: '0.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{t('overview.total', 'Total')}</strong>
                <strong>{nextMonthTotalWithInstallments.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
              </div>
            </div>
          </div>
        )}
        </div> {/* end .dashboard-main */}
                <footer className="zenda-footer-legend" aria-label="Zenda version legend">
          <span>Zenda · v{APP_VERSION}</span>
        </footer>
      </div>
    </div>
  );
}